import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';

import type {TimeRegistrationMutationInput} from './logic/TimeRegistrationModalMutation';
import {updateTimeRegistration} from './logic/TimeRegistrationModalMutation';
import TimeRegistrationModalQueryWrapper from './query-wrappers/TimeRegistrationModalQueryWrapper';
import {toEntityId} from './util/TimeRegistrationModalUtil';

import type {TimeRegistrationEntityId} from './types/TimeRegistrationEntityId';
import type {TimeRegistrationModalInfo} from './types/TimeRegistrationModalInfo';
import type {TimeRegistrationModalUpdate_viewer$data as TimeRegistrationModalUpdate_viewer} from './__generated__/TimeRegistrationModalUpdate_viewer.graphql';
import {Role} from './types/TimeRegistrationRole';

type TimeRegistrationModalUpdate_timeRegistration = TimeRegistrationModalUpdate_viewer['timeRegistration'];

const infoText: TimeRegistrationModalInfo = {
	headerText: 'Update time entry',
	creativeButtonText: 'Save',
};

type UpdateModalTimeRegistrationInput = {
	timeRegistrationMutationInput: TimeRegistrationMutationInput;
	timeRegistrationId: string | null;
	closeModal: () => void;
};
function updateModalTimeRegistration({
	timeRegistrationMutationInput,
	timeRegistrationId,
	closeModal,
}: UpdateModalTimeRegistrationInput): void {
	if (timeRegistrationId) {
		updateTimeRegistration({timeRegistrationId, ...timeRegistrationMutationInput});
		closeModal();
	}
}

function getEntityIdFromTimeRegistration(
	timeRegistration: TimeRegistrationModalUpdate_timeRegistration
): TimeRegistrationEntityId | null {
	if (!timeRegistration) return null;

	const {taskId, projectId, idleTimeId} = timeRegistration;
	return toEntityId({taskId, projectId, idleTimeId});
}

function getInitialDateFromTimeRegistration(timeRegistration: TimeRegistrationModalUpdate_timeRegistration): Date | undefined {
	if (timeRegistration && timeRegistration.year && timeRegistration.month && timeRegistration.day) {
		return new Date(timeRegistration.year, timeRegistration.month - 1, timeRegistration.day);
	}
}

type TimeRegistrationModalUpdateProps = {
	viewer: TimeRegistrationModalUpdate_viewer;
	closeModal: () => void;
};
function TimeRegistrationModalUpdate({viewer, closeModal}: TimeRegistrationModalUpdateProps) {
	const timeRegistration = viewer.timeRegistration;
	const timeRegistrationId = timeRegistration ? timeRegistration.id : null;
	const entityId = getEntityIdFromTimeRegistration(timeRegistration);
	const projectId = timeRegistration?.task?.project?.id;
	const timeRegBillableTime = timeRegistration?.billableMinutesRegistered ?? undefined;

	const personId = timeRegistration?.person?.id || viewer.actualPersonId;
	const initialTime = timeRegistration?.minutesRegistered || undefined;
	const jiraTaskId = timeRegistration?.task?.jiraId || undefined;

	const initialState = {
		minutes: initialTime,
		note: timeRegistration?.notes || undefined,
		date: getInitialDateFromTimeRegistration(timeRegistration),
		// We only pass along a value in initialBillableTime if minutesRegistered and billableMinutesRegistered are not the same.
		// If they are the same, we consider them to be linked and will let the default handling take over.
		billableTime: initialTime !== timeRegBillableTime ? timeRegBillableTime : undefined,
		harvestTaskId: timeRegistration?.harvestTask?.id || undefined,
		role: (timeRegistration?.role as Role) || undefined,
	};

	return (
		<TimeRegistrationModalQueryWrapper
			personId={personId}
			entityId={entityId}
			projectId={projectId}
			infoText={infoText}
			initialState={initialState}
			addInitialTimeToRemaining={true}
			jiraTaskId={jiraTaskId}
			onlyValidateFormElementsOnChange
			callbackPositive={timeRegistrationMutationInput =>
				updateModalTimeRegistration({timeRegistrationMutationInput, timeRegistrationId, closeModal})
			}
			closeModal={closeModal}
		/>
	);
}

export const TimeRegistrationModalUpdateQuery = graphql`
	query TimeRegistrationModalUpdate_Query($timeRegistrationId: ID!) {
		viewer {
			actualPersonId
			component(name: "time_registration_modal_update")
			...TimeRegistrationModalUpdate_viewer @arguments(timeRegistrationId: $timeRegistrationId)
		}
	}
`;

export default createFragmentContainer(TimeRegistrationModalUpdate, {
	viewer: graphql`
		fragment TimeRegistrationModalUpdate_viewer on Viewer @argumentDefinitions(timeRegistrationId: {type: "ID!"}) {
			actualPersonId
			timeRegistration(id: $timeRegistrationId) {
				id
				taskId
				projectId
				idleTimeId
				minutesRegistered
				billableMinutesRegistered
				notes
				year
				month
				day
				person {
					id
				}
				harvestTask {
					id
				}
				role {
					id
					name
				}
				task {
					id
					jiraId
					project {
						id
					}
				}
			}
		}
	`,
});
